import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  { range: "1–4 vials", label: "Standard Research", note: "Listed per-vial pricing" },
  { range: "5–9 vials", label: "Volume Tier", note: "~15% volume adjustment per compound" },
  { range: "10+ vials", label: "Bulk / Clinic Tier", note: "~28% wholesale adjustment per compound" },
];

export default function Wholesale() {
  const [form, setForm] = useState({ name: "", entity: "", email: "", volume: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Wholesale inquiry received — our compliance desk will respond shortly");
      setForm({ name: "", entity: "", email: "", volume: "", message: "" });
    } catch {
      toast.error("Could not submit inquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="wholesale" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24" data-testid="wholesale-section">
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <p className="font-tech text-xs uppercase tracking-[0.18em] text-sky-700 mb-2">Institutional Accounts</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
            Wholesale Programs for Labs &amp; Research Clinics
          </h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            Volume pricing applies automatically in your order request. For standing supply agreements,
            custom batch reservations, or recurring institutional orders, contact our wholesale desk.
          </p>
          <div className="mt-8 border border-slate-200 bg-white">
            {TIERS.map((t, i) => (
              <div key={t.range} className={`flex items-center justify-between p-5 ${i < TIERS.length - 1 ? "border-b border-slate-200" : ""}`}>
                <div>
                  <p className="font-tech text-sm font-semibold text-slate-900">{t.range}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{t.label}</p>
                </div>
                <p className="text-sm text-slate-600 text-right">{t.note}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 font-tech text-[11px] uppercase tracking-[0.16em] text-amber-700 bg-amber-50 border border-amber-200 p-3">
            All institutional orders remain strictly Research Use Only and require the same signed compliance attestation.
          </p>
        </div>
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 sm:p-8 space-y-4 h-fit" data-testid="wholesale-inquiry-form">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="h-5 w-5 text-sky-700" />
            <h3 className="font-display font-semibold text-lg text-slate-900">Wholesale Inquiry</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label htmlFor="wi-name">Contact Name</Label>
              <Input id="wi-name" required value={form.name} onChange={set("name")} data-testid="wholesale-inquiry-name" className="rounded-sm mt-1.5" /></div>
            <div><Label htmlFor="wi-entity">Lab / Entity Name</Label>
              <Input id="wi-entity" value={form.entity} onChange={set("entity")} data-testid="wholesale-inquiry-entity" className="rounded-sm mt-1.5" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label htmlFor="wi-email">Email</Label>
              <Input id="wi-email" type="email" required value={form.email} onChange={set("email")} data-testid="wholesale-inquiry-email" className="rounded-sm mt-1.5" /></div>
            <div><Label htmlFor="wi-volume">Estimated Monthly Volume</Label>
              <Input id="wi-volume" placeholder="e.g. 50 vials" value={form.volume} onChange={set("volume")} data-testid="wholesale-inquiry-volume" className="rounded-sm mt-1.5" /></div>
          </div>
          <div><Label htmlFor="wi-message">Research Context &amp; Requirements</Label>
            <Textarea id="wi-message" rows={4} value={form.message} onChange={set("message")} data-testid="wholesale-inquiry-message" className="rounded-sm mt-1.5" /></div>
          <Button type="submit" disabled={sending} data-testid="wholesale-inquiry-form-submit" className="w-full gap-2">
            <Send className="h-4 w-4" /> {sending ? "Submitting..." : "Submit Wholesale Inquiry"}
          </Button>
        </form>
      </div>
    </section>
  );
}
